import {
  ArrowUpRight,
  Chat,
  type Icon,
  ThumbsUp,
  UsersThree,
} from "@phosphor-icons/react";
import clsx from "clsx";

import Skeleton from "common/components/ui/Skeleton";
import Switch from "common/components/ui/Switch";
import { typedObjectEntries } from "common/helpers/utils";

import type { PostInteractions, PostStatus } from "../../../../../types";

interface PostStatsDetails {
  postStatus: PostStatus;
  interactions: PostInteractions;
  isLoading: boolean;
  updatePostStatus: (status: keyof PostStatus, checked: boolean) => void;
}

interface PostDetailsInfo {
  icon: Icon;
  label: string;
  description: string;
  amount: number;
  isChecked: boolean;
}

export default function PostStats({
  postStatus,
  interactions,
  isLoading,
  updatePostStatus,
}: PostStatsDetails) {
  const { isLiked, isCommented, isShared } = postStatus;
  const { likes, shares, comments } = interactions;

  const postStatuses: Record<keyof PostStatus, PostDetailsInfo> = {
    isLiked: {
      icon: ThumbsUp,
      label: "Likes",
      description: "People that liked the post",
      amount: likes,
      isChecked: isLiked,
    },
    isCommented: {
      icon: Chat,
      label: "Comments",
      description: "People that commented the post",
      amount: comments,
      isChecked: isCommented,
    },
    isShared: {
      icon: ArrowUpRight,
      label: "Reposts",
      description: "People that reposted the post",
      amount: shares,
      isChecked: isShared,
    },
  };

  const totalAmount = typedObjectEntries(postStatuses)
    .map(([, post]) => post)
    .reduce((total, { amount, isChecked }) => {
      if (isChecked) {
        return total + amount;
      }
      return total;
    }, 0);

  return (
    <article className="flex flex-col gap-y-3 rounded-3xl border border-black-200 p-3">
      {/* Heading */}
      <div className="flex flex-col gap-y-1">
        <h3 className="text-lg-bold">Post stats</h3>
        <p className="text-black-500">Select at least one</p>
      </div>

      <div className="-mx-3 flex flex-col divide-y divide-black-200 border-y border-black-200 px-3">
        {typedObjectEntries(postStatuses).map(
          ([key, { icon: Icon, label, description, amount, isChecked }]) => (
            <div key={key} className="flex items-center gap-3 py-3">
              <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-2xl bg-black-100">
                <Icon size={20} />
              </span>

              <div className="flex-1">
                <h5 className="mb-0.5 text-button-md">{label}</h5>
                <p className="text-xs text-black-500">{description}</p>
              </div>

              <div
                className={clsx(
                  "flex items-center transition-colors",
                  isChecked ? "text-black-950" : "text-black-400",
                )}
              >
                <UsersThree size={20} weight="fill" className="mr-1" />
                {isLoading ? (
                  <Skeleton className="size-6" />
                ) : (
                  <span className="text-button-sm">{amount}</span>
                )}
                <Switch
                  className="ml-3"
                  checked={isChecked}
                  disabled={isLoading || !amount}
                  onCheckedChange={(checked) => updatePostStatus(key, checked)}
                />
              </div>
            </div>
          ),
        )}
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h5 className="text-lg-bold">Total people</h5>
          <p className="text-black-500">Select at least one</p>
        </div>
        <div className="flex items-center gap-1">
          <UsersThree size={20} weight="fill" />
          {isLoading ? (
            <Skeleton className="size-6" />
          ) : (
            <span className="text-button-sm">{totalAmount}</span>
          )}
        </div>
      </div>
    </article>
  );
}
